"use client";

import Title from "@/components/ui/title";
import { cn } from "@/lib/utils";
import { useScopedI18n } from "@/messages/client";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import createGlobe from "cobe";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";

const globeOptions = {
  devicePixelRatio: 2,
  phi: 0,
  theta: 0,
  dark: 0,
  diffuse: 1.9,
  mapSamples: 16000,
  mapBrightness: 5,
  // mapBaseBrightness: 0.05,
  baseColor: [0.9, 0.9, 0.9] as [number, number, number],
  markerColor: [1, 0.31, 0] as [number, number, number],
  glowColor: [0.909, 0.909, 0.909] as [number, number, number],
  scale: 1.25,
  // opacity: 0.6,
};

function diffRange(current: number, target: number, length: number) {
  if (current === target || !length) {
    return 0;
  }
  const greater = Math.max(current, target);
  const leasser = Math.min(current, target);
  const directDiff = greater - leasser;
  const indirectDiff = leasser + length - greater;
  return Math.min(directDiff, indirectDiff);
}

export default function EnterpriseScale(props: {
  data?: {
    country?: string;
    lang?: number;
    lat?: number;
    cities?: {
      name?: string;
      name_en?: string;
      lang?: number;
      lat?: number;
    }[];
  }[];
}) {
  const t = useScopedI18n("POWERQUARK简介");

  const locale = useCurrentLocale();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const globe = useRef<any>(null);

  const [current, setCurrent] = useState(0);
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);

  const markers = useMemo(() => {
    const arr: {
      location: [number, number];
      size: number;
    }[] = [];
    props.data?.forEach((el) => {
      el.cities?.forEach((item) => {
        if (typeof item.lang === "number" && typeof item.lat === "number") {
          arr.push({
            location: [item.lat, item.lang],
            size: 0.06,
          });
        }
      });
      if (el.lang && el.lat) {
        arr.push({
          location: [el.lat, el.lang],
          size: 0.06,
        });
      }
    });
    return arr;
  }, [props.data]);

  const cities = useMemo(
    () => props.data?.[current]?.cities ?? [],
    [props.data, current]
  );

  const allCities = useMemo(
    () => props.data?.flatMap((item) => item.cities)?.filter((item) => item),
    [props.data]
  );

  const onSelect = useCallback(() => {
    if (!mainApi) {
      return;
    }
    setCurrent(mainApi.selectedScrollSnap() || 0);
  }, [mainApi]);

  useEffect(() => {
    if (!mainApi) {
      return;
    }
    onSelect();
    mainApi.on("select", onSelect).on("reInit", onSelect);
  }, [mainApi, onSelect]);

  useEffect(() => {
    const listener = () => {
      if (canvasRef.current) {
        if (window.matchMedia("(min-width: 1024px)").matches) {
          if (globe.current) {
            globe.current.destroy();
          }
          let phi = 0;
          globe.current = createGlobe(canvasRef.current, {
            ...globeOptions,
            width: 500 * 2,
            height: 500 * 2,
            markers,
            onRender: (state) => {
              state.phi = phi;
              phi += 0.005;
            },
          });
        } else if (window.matchMedia("(min-width: 768px)").matches) {
          if (globe.current) {
            globe.current.destroy();
          }
          let phi = 0;
          globe.current = createGlobe(canvasRef.current, {
            ...globeOptions,
            width: 360 * 2,
            height: 360 * 2,
            markers,
            onRender: (state) => {
              state.phi = phi;
              phi += 0.005;
            },
          });
          console.log("render");
        }
      }
    };
    listener();
    window.addEventListener("resize", listener);
    return () => {
      window.removeEventListener("resize", listener);
      if (globe.current) {
        console.log("destroy");
        globe.current.destroy();
      }
    };
  }, []);

  return (
    <div className="w-full max-md:-ml-6 max-md:box-content max-md:bg-white max-md:px-6">
      <Title title={t("企业规模")} underline={false} />
      <div className="mt-5 flex items-center justify-between md:mt-8">
        <div className="flex-auto lg:max-w-[450px]">
          <h4 className="text-center text-base font-bold md:text-left md:text-[32px] md:leading-normal">
            {t("企业规模标题")}
          </h4>
          {/* <p className="mt-1 text-center text-sm text-[#636363] max-md:px-10 md:text-left md:text-base">
            {t("企业规模描述")}
          </p> */}
          <div className={cn(
            "mt-5 flex h-[260px] w-full rounded-[10px] bg-white md:mt-16 md:h-[300px]",
            {
              "w-[150px]" : !allCities?.length,
            }
          )}>
            <div className={
              cn(
                "h-full w-[150px] shrink-0 rounded-l-[10px] bg-zinc-50 shadow-[0_0_14px_rgba(9,0,0,0.08)] md:w-[170px]",
                {
                  "rounded-r-[10px]": !allCities?.length,
                }
              )
            }>
              <Carousel
                className="flex h-full w-full flex-col justify-center overflow-hidden"
                orientation="vertical"
                opts={{ loop: true, watchDrag: false }}
                setApi={setMainApi}>
                <CarouselContent
                  className="m-0"
                  containerClassName="overflow-visible h-4/5">
                  {props.data?.map((el, index) => (
                    <CarouselItem key={index} className="basis-1/4 p-0">
                      <div
                        onClick={() => {
                          mainApi?.scrollTo(index);

                          setCurrent(index);
                        }}
                        className={cn(
                          "flex h-full w-full select-none items-center justify-center text-center duration-300",
                          {
                            "cursor-pointer": index !== current,
                            "text-[26px] ": index === current,
                            "text-lg opacity-50":
                              diffRange(
                                current,
                                index,
                                props.data?.length || 0
                              ) === 1,
                            "text-xs opacity-20":
                              diffRange(
                                current,
                                index,
                                props.data?.length || 0
                              ) >= 2,
                          }
                        )}>
                        {el.country}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
            {!!allCities?.length && (
              <div className="relative z-10 h-full flex-auto rounded-r-[10px] bg-white py-5 pr-2 shadow-[0_0_14px_rgba(0,0,0,0.08)] md:py-7 md:pr-11">
                <div className="flex h-full w-full flex-wrap items-center gap-y-5 overflow-y-auto">
                  {cities.map((el, index) => (
                    <Fragment key={`city_${current}_${index}`}>
                      <div className="flex w-[calc(50%-1px)] flex-col items-center">
                        {locale === "cn" ? (
                          <>
                            <span className="text-sm md:text-lg md:leading-tight">
                              {el.name}
                            </span>
                            <span className="text-xs text-zinc-400 md:text-sm md:leading-tight">
                              {el.name_en}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="text-sm md:text-lg md:leading-tight">
                              {el.name_en}
                            </span>
                          </>
                        )}
                      </div>
                      {index % 2 === 0 && index !== cities.length - 1 && (
                        <hr className="h-7 border-l border-zinc-400" />
                      )}
                    </Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 max-md:hidden md:h-[360px] md:w-[360px] lg:h-[500px] lg:w-[500px]">
          <canvas ref={canvasRef} className="h-full w-full"></canvas>
        </div>
      </div>
    </div>
  );
}
